import {
    createColumns,
    DataTableColumn,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import dayjs from 'dayjs'
import {
    Download,
    FileSpreadsheet,
    Minus,
    TrendingDown,
    TrendingUp,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import * as XLSX from 'xlsx'
import {
    calculateChange,
    calculateGrowthRate,
    getServiceMonthlyReportExport,
} from '../services'
import {
    MonthlyExportFilters,
    MonthlyExportItem,
    ServiceExportDetailBase,
    ServiceExportDetailWithAction,
    ServiceMonthlyReportItem,
} from '../types'

interface MonthlyDataTableProps {
    data: ServiceMonthlyReportItem[]
    previousYearData?: ServiceMonthlyReportItem[]
    showComparison: boolean
    year: number
    filters?: Omit<MonthlyExportFilters, 'year'>
}

interface TableRowData extends ServiceMonthlyReportItem {
    monthLabel: string
    prevItem?: ServiceMonthlyReportItem
    startingGrowth: number | null
    newGrowth: number | null
    cancelledGrowth: number | null
    cancelledCustomersGrowth: number | null
    cancelledAmountGrowth: number | null
    endingGrowth: number | null
    cancellationRateChange: number | null
}

const MonthlyDataTable: React.FC<MonthlyDataTableProps> = ({
    data,
    previousYearData,
    showComparison,
    year,
    filters,
}) => {
    const { t } = useTranslation()
    const [isExportingDetail, setIsExportingDetail] = useState(false)

    const tableData = useMemo<TableRowData[]>(() => {
        return data.map((item) => {
            const prevItem = previousYearData?.find(
                (p) => p.month.slice(5) === item.month.slice(5)
            )
            return {
                ...item,
                monthLabel: dayjs(item.month + '-01').format('MMMM'),
                prevItem,
                startingGrowth: calculateGrowthRate(
                    item.starting_services,
                    prevItem?.starting_services
                ),
                newGrowth: calculateGrowthRate(
                    item.new_services,
                    prevItem?.new_services
                ),
                cancelledGrowth: calculateGrowthRate(
                    item.cancelled_services,
                    prevItem?.cancelled_services
                ),
                cancelledCustomersGrowth: calculateGrowthRate(
                    item.cancelled_customers,
                    prevItem?.cancelled_customers
                ),
                cancelledAmountGrowth: calculateGrowthRate(
                    item.cancelled_amount,
                    prevItem?.cancelled_amount
                ),
                endingGrowth: calculateGrowthRate(
                    item.ending_services,
                    prevItem?.ending_services
                ),
                cancellationRateChange: calculateChange(
                    item.cancellation_rate,
                    prevItem?.cancellation_rate
                ),
            }
        })
    }, [data, previousYearData])

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            minimumFractionDigits: 0,
        }).format(amount)
    }

    const renderGrowthCell = (
        growth: number | null,
        isNegativeGood: boolean = false
    ) => {
        if (growth === null) return <span className="text-gray-400">-</span>

        if (growth > 0) {
            return (
                <div
                    className={`flex items-center justify-end gap-0.5 ${isNegativeGood ? 'text-red-600' : 'text-green-600'}`}
                >
                    <TrendingUp size={12} />
                    <span className="text-xs font-medium">
                        +{growth.toFixed(1)}%
                    </span>
                </div>
            )
        } else if (growth < 0) {
            return (
                <div
                    className={`flex items-center justify-end gap-0.5 ${isNegativeGood ? 'text-green-600' : 'text-red-600'}`}
                >
                    <TrendingDown size={12} />
                    <span className="text-xs font-medium">
                        {growth.toFixed(1)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center justify-end gap-0.5 text-gray-500">
                <Minus size={12} />
                <span className="text-xs font-medium">0%</span>
            </div>
        )
    }

    const renderRateChangeCell = (change: number | null) => {
        if (change === null) return <span className="text-gray-400">-</span>

        if (change > 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-red-600">
                    <TrendingUp size={12} />
                    <span className="text-xs font-medium">
                        +{change.toFixed(2)}%
                    </span>
                </div>
            )
        } else if (change < 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-green-600">
                    <TrendingDown size={12} />
                    <span className="text-xs font-medium">
                        {change.toFixed(2)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center justify-end gap-0.5 text-gray-500">
                <Minus size={12} />
                <span className="text-xs font-medium">0%</span>
            </div>
        )
    }

    // Build columns dynamically based on showComparison
    const columns = useMemo<DataTableColumn<TableRowData>[]>(() => {
        const cols: DataTableColumn<TableRowData>[] = [
            {
                key: 'monthLabel',
                title: t('month'),
                width: 120,
                headerClassName: 'text-left',
                cellClassName: 'font-medium capitalize',
            },
            {
                key: 'starting_services',
                title: t('starting'),
                width: 100,
                headerClassName: 'text-right',
                cellClassName: 'text-right text-gray-600',
                render: (value) => value.toLocaleString(),
            },
            {
                key: 'new_services',
                title: t('new'),
                width: 100,
                headerClassName: 'text-right text-green-600',
                cellClassName: 'text-right text-green-600 font-medium',
                render: (value) => `+${value.toLocaleString()}`,
            },
        ]

        if (showComparison) {
            cols.push({
                key: 'newGrowth',
                title: t('reports.services.growth_rate_of_new_services'),
                width: 80,
                headerClassName: 'text-right text-green-400',
                cellClassName: 'text-right',
                render: (value) => renderGrowthCell(value),
            })
        }

        cols.push({
            key: 'cancelled_services',
            title: t('cancelled'),
            width: 100,
            headerClassName: 'text-right text-red-600',
            cellClassName: 'text-right text-red-600 font-medium',
            render: (value) => `-${value.toLocaleString()}`,
        })

        if (showComparison) {
            cols.push({
                key: 'cancelledGrowth',
                title: t('reports.services.growth_rate_of_cancelled_services'),
                width: 80,
                headerClassName: 'text-right text-red-400',
                cellClassName: 'text-right',
                render: (value) => renderGrowthCell(value, true),
            })
        }

        cols.push(
            {
                key: 'cancelled_customers',
                title: t('customers'),
                width: 100,
                headerClassName: 'text-right text-orange-600',
                cellClassName: 'text-right text-orange-600',
                render: (value) => value.toLocaleString(),
            },
            {
                key: 'cancelled_amount',
                title: t('reports.services.cancelled_amount'),
                width: 140,
                headerClassName: 'text-right text-c-error',
                cellClassName: 'text-right text-c-error',
                render: (value) => formatCurrency(value),
            },
            {
                key: 'ending_services',
                title: t('ending'),
                width: 100,
                headerClassName: 'text-right text-blue-600',
                cellClassName: 'text-right text-blue-600 font-bold',
                render: (value) => value.toLocaleString(),
            }
        )

        if (showComparison) {
            cols.push({
                key: 'endingGrowth',
                title: t('reports.services.growth_rate_of_ending_services'),
                width: 80,
                headerClassName: 'text-right text-blue-400',
                cellClassName: 'text-right',
                render: (value) => renderGrowthCell(value),
            })
        }

        cols.push({
            key: 'cancellation_rate',
            title: t('cancel_%'),
            width: 100,
            headerClassName: 'text-right text-rose-600',
            cellClassName: 'text-right text-rose-600',
            render: (value) => `${value.toFixed(2)}%`,
        })

        if (showComparison) {
            cols.push({
                key: 'cancellationRateChange',
                title: t('change'),
                width: 80,
                headerClassName: 'text-right text-rose-400',
                cellClassName: 'text-right',
                render: (value) => renderRateChangeCell(value),
            })
        }

        return createColumns<TableRowData>(cols)
    }, [t, showComparison])

    // Footer row with totals/averages
    const footerRow = useMemo(() => {
        const totalNew = data.reduce((s, d) => s + d.new_services, 0)
        const totalCancelled = data.reduce(
            (s, d) => s + d.cancelled_services,
            0
        )
        const totalCancelledCustomers = data.reduce(
            (s, d) => s + d.cancelled_customers,
            0
        )
        const totalCancelledAmount = data.reduce(
            (s, d) => s + d.cancelled_amount,
            0
        )
        const avgCancellationRate =
            data.reduce((s, d) => s + d.cancellation_rate, 0) /
            (data.length || 1)

        const cells: Record<string, React.ReactNode> = {
            monthLabel: `${t('total')} / ${t('average')}`,
            starting_services:
                data[0]?.starting_services.toLocaleString() || '-',
            new_services: (
                <span className="text-green-700">
                    +{totalNew.toLocaleString()}
                </span>
            ),
            cancelled_services: (
                <span className="text-red-700">
                    -{totalCancelled.toLocaleString()}
                </span>
            ),
            cancelled_customers: (
                <span className="text-orange-700">
                    {totalCancelledCustomers.toLocaleString()}
                </span>
            ),
            cancelled_amount: (
                <span className="text-c-error">
                    {formatCurrency(totalCancelledAmount)}
                </span>
            ),
            ending_services: (
                <span className="text-blue-700 font-bold">
                    {data[data.length - 1]?.ending_services.toLocaleString() ||
                        '-'}
                </span>
            ),
            cancellation_rate: (
                <span className="text-rose-700">
                    {avgCancellationRate.toFixed(2)}%
                </span>
            ),
        }

        if (showComparison) {
            const prevTotalNew =
                previousYearData?.reduce((s, d) => s + d.new_services, 0) || 0
            const prevTotalCancelled =
                previousYearData?.reduce(
                    (s, d) => s + d.cancelled_services,
                    0
                ) || 0
            const prevEnding =
                previousYearData?.[previousYearData.length - 1]?.ending_services
            const prevAvgCancellationRate = previousYearData
                ? previousYearData.reduce(
                      (s, d) => s + d.cancellation_rate,
                      0
                  ) / (previousYearData.length || 1)
                : undefined

            cells.newGrowth = renderGrowthCell(
                calculateGrowthRate(totalNew, prevTotalNew)
            )
            cells.cancelledGrowth = renderGrowthCell(
                calculateGrowthRate(totalCancelled, prevTotalCancelled),
                true
            )
            cells.endingGrowth = renderGrowthCell(
                calculateGrowthRate(
                    data[data.length - 1]?.ending_services || 0,
                    prevEnding
                )
            )
            cells.cancellationRateChange = renderRateChangeCell(
                calculateChange(avgCancellationRate, prevAvgCancellationRate)
            )
        }

        return { cells, cellClassName: 'text-right' }
    }, [data, previousYearData, showComparison, t])

    // Export functions
    const exportToExcel = () => {
        const exportData = tableData.map((item) => ({
            [t('month')]: item.monthLabel,
            [t('starting_services')]: item.starting_services,
            ...(showComparison && item.prevItem
                ? {
                      [`${t('starting_services')} ${year - 1}`]:
                          item.prevItem.starting_services,
                      [`${t('starting_services')} %`]:
                          item.startingGrowth !== null
                              ? `${item.startingGrowth.toFixed(1)}%`
                              : '-',
                  }
                : {}),
            [t('new_services')]: item.new_services,
            ...(showComparison && item.prevItem
                ? {
                      [`${t('new_services')} ${year - 1}`]:
                          item.prevItem.new_services,
                      [`${t('new_services')} %`]:
                          item.newGrowth !== null
                              ? `${item.newGrowth.toFixed(1)}%`
                              : '-',
                  }
                : {}),
            [t('cancelled_services')]: item.cancelled_services,
            ...(showComparison && item.prevItem
                ? {
                      [`${t('cancelled_services')} ${year - 1}`]:
                          item.prevItem.cancelled_services,
                      [`${t('cancelled_services')} %`]:
                          item.cancelledGrowth !== null
                              ? `${item.cancelledGrowth.toFixed(1)}%`
                              : '-',
                  }
                : {}),
            [t('cancelled_customers')]: item.cancelled_customers,
            [t('cancelled_amount')]: item.cancelled_amount,
            [t('ending_services')]: item.ending_services,
            ...(showComparison && item.prevItem
                ? {
                      [`${t('ending_services')} ${year - 1}`]:
                          item.prevItem.ending_services,
                      [`${t('ending_services')} %`]:
                          item.endingGrowth !== null
                              ? `${item.endingGrowth.toFixed(1)}%`
                              : '-',
                  }
                : {}),
            [t('cancellation_rate')]: `${item.cancellation_rate.toFixed(2)}%`,
            [t('retention_rate')]: `${item.retention_rate.toFixed(2)}%`,
        }))

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        XLSX.utils.book_append_sheet(wb, ws, t('monthly_data'))
        XLSX.writeFile(wb, `service_monthly_report_${year}.xlsx`)
    }

    const formatEmployeeNames = (
        employees: { code: number; name: string; status: number }[]
    ) => {
        return employees.map((e) => e.name).join(', ')
    }

    const createServiceRow = (
        service: ServiceExportDetailBase | ServiceExportDetailWithAction,
        month: string,
        type: 'starting' | 'new' | 'cancelled'
    ) => {
        const baseRow: Record<string, string | number | boolean> = {
            [t('month')]: dayjs(month + '-01').format('MMMM YYYY'),
            [t('reports.services.type')]: t(
                `reports.services.${type}_services`
            ),
            [t('reports.services.service_id')]: service.service_id,
            [t('reports.services.service_name')]: service.service_name,
            [t('reports.services.product_group')]: service.product_group_name,
            [t('reports.services.customer_id')]: service.customer_id,
            [t('reports.services.customer_name')]: service.customer_name,
            [t('status')]: service.status,
            [t('reports.services.is_poc')]: service.is_poc ? t('yes') : t('no'),
            [t('reports.services.start_date')]: service.start_date,
            [t('reports.services.end_date')]: service.end_date || '-',
            [t('reports.services.current_amount')]: service.current_amount,
            [t('reports.services.account_managers')]: formatEmployeeNames(
                service.account_managers || []
            ),
            [t('reports.services.sale_managers')]: formatEmployeeNames(
                service.sale_managers || []
            ),
        }

        if ('action_type' in service) {
            baseRow[t('reports.services.action_type')] = service.action_type
            baseRow[t('reports.services.effective_at')] = service.effective_at
            baseRow[t('reports.services.action_amount')] = service.action_amount
            baseRow[t('reports.services.action_note')] =
                service.action_note || '-'
        }

        return baseRow
    }

    const exportDetailToExcel = async () => {
        setIsExportingDetail(true)
        try {
            const exportData = await getServiceMonthlyReportExport({
                year,
                ...filters,
            })

            if (!exportData || exportData.length === 0) {
                setIsExportingDetail(false)
                return
            }

            const wb = XLSX.utils.book_new()

            // Sheet 1: Summary
            const summaryData = exportData.map((item: MonthlyExportItem) => ({
                [t('month')]: dayjs(item.month + '-01').format('MMMM YYYY'),
                [t('starting_services')]: item.summary.starting_services,
                [t('new_services')]: item.summary.new_services,
                [t('cancelled_services')]: item.summary.cancelled_services,
                [t('cancelled_customers')]: item.summary.cancelled_customers,
                [t('cancelled_amount')]: item.summary.cancelled_amount,
                [t('ending_services')]: item.summary.ending_services,
                [t('cancellation_rate')]:
                    `${item.summary.cancellation_rate.toFixed(2)}%`,
                [t('retention_rate')]:
                    `${item.summary.retention_rate.toFixed(2)}%`,
            }))
            const summaryWs = XLSX.utils.json_to_sheet(summaryData)
            XLSX.utils.book_append_sheet(
                wb,
                summaryWs,
                t('reports.services.summary')
            )

            // Sheet 2: Starting Services
            const startingServices: Record<
                string,
                string | number | boolean
            >[] = []
            exportData.forEach((item: MonthlyExportItem) => {
                ;(item.starting_services || []).forEach((service) => {
                    startingServices.push(
                        createServiceRow(service, item.month, 'starting')
                    )
                })
            })
            if (startingServices.length > 0) {
                const startingWs = XLSX.utils.json_to_sheet(startingServices)
                XLSX.utils.book_append_sheet(
                    wb,
                    startingWs,
                    t('reports.services.starting_services')
                )
            }

            // Sheet 3: New Services
            const newServices: Record<string, string | number | boolean>[] = []
            exportData.forEach((item: MonthlyExportItem) => {
                ;(item.new_services || []).forEach((service) => {
                    newServices.push(
                        createServiceRow(service, item.month, 'new')
                    )
                })
            })
            if (newServices.length > 0) {
                const newWs = XLSX.utils.json_to_sheet(newServices)
                XLSX.utils.book_append_sheet(
                    wb,
                    newWs,
                    t('reports.services.new_services')
                )
            }

            // Sheet 4: Cancelled Services
            const cancelledServices: Record<
                string,
                string | number | boolean
            >[] = []
            exportData.forEach((item: MonthlyExportItem) => {
                ;(item.cancelled_services || []).forEach((service) => {
                    cancelledServices.push(
                        createServiceRow(service, item.month, 'cancelled')
                    )
                })
            })
            if (cancelledServices.length > 0) {
                const cancelledWs = XLSX.utils.json_to_sheet(cancelledServices)
                XLSX.utils.book_append_sheet(
                    wb,
                    cancelledWs,
                    t('reports.services.cancelled_services')
                )
            }

            XLSX.writeFile(wb, `service_monthly_report_detail_${year}.xlsx`)
        } catch (error) {
            console.error('Failed to export detail:', error)
        } finally {
            setIsExportingDetail(false)
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div>
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('monthly_detail_data')}
                    </Typogaphy>
                    <Typogaphy className="text-sm text-gray-500">
                        {t('service_statistics_by_month')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportDetailToExcel}
                        className="!min-w-0 !px-3"
                        disabled={isExportingDetail}
                    >
                        <FileSpreadsheet size={16} className="mr-1" />
                        {isExportingDetail
                            ? t('reports.services.exporting')
                            : t('reports.services.export_detail')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-3"
                    >
                        <Download size={16} className="mr-1" />
                        {t('export_excel')}
                    </Button>
                </div>
            </div>

            <div className="p-4">
                <DataTableCustomized
                    columns={columns}
                    rows={tableData}
                    rowKey={(row) => row.month}
                    footerRow={footerRow}
                    size="small"
                />
            </div>
        </div>
    )
}

export default MonthlyDataTable
